import { z } from 'zod';
import {
  ApplyOutcomeSchema,
  ConfigObjectSchema,
  MaterialEntrySchema,
  RedactedFieldSchema,
  ResourceIdSchema,
  ResourceSummarySchema,
  SchemaDescriptorSchema,
  ValidateOutcomeSchema,
} from './schema.js';

/**
 * Bridge <-> backend WebSocket protocol, version 1. See docs/API_CONTRACT.md for the full flow.
 * Every frame is one UTF-8 JSON text message containing an Envelope.
 */

export const PROTOCOL_VERSION = 1;
export const SUPPORTED_PROTOCOLS = [1] as const;

/** Largest accepted frame in either direction, in bytes of UTF-8 JSON. */
export const MAX_MESSAGE_BYTES = 1_048_576;

/** Text signed by the bridge's Ed25519 key in HELLO: AUTH_SIGNATURE_PREFIX + nonce + '\n' + installationId. */
export const AUTH_SIGNATURE_PREFIX = 'comficonf-bridge-auth:v1\n';

/** Path of the bridge WebSocket endpoint on the backend. */
export const BRIDGE_WS_PATH = '/api/v1/bridge';

export const DEFAULT_HEARTBEAT_INTERVAL_MS = 20_000;
export const DEFAULT_HEARTBEAT_TIMEOUT_MS = 60_000;

/** WebSocket close codes used by both sides. */
export const CloseCodes = {
  NORMAL: 1000,
  GOING_AWAY: 1001,
  POLICY_VIOLATION: 1008,
  MESSAGE_TOO_BIG: 1009,
  AUTH_FAILED: 4001,
  INSTALLATION_REVOKED: 4003,
  UNSUPPORTED_PROTOCOL: 4004,
  HEARTBEAT_TIMEOUT: 4008,
  REPLACED: 4009,
  HANDSHAKE_TIMEOUT: 4010,
} as const;

export const MESSAGE_TYPES = [
  'CHALLENGE',
  'HELLO',
  'WELCOME',
  'HEARTBEAT',
  'PAIRING_REQUEST',
  'PAIRED',
  'REVOKED',
  'LIST_RESOURCES',
  'GET_SCHEMA',
  'READ_CONFIG',
  'VALIDATE_CONFIG',
  'APPLY_CONFIG',
  'GET_CATALOG',
  'RESOURCES_CHANGED',
  'RESULT',
  'ERROR',
] as const;
export const MessageTypeSchema = z.enum(MESSAGE_TYPES);
export type MessageType = z.infer<typeof MessageTypeSchema>;

/** Requests the backend sends to a paired bridge; each is answered by RESULT or ERROR. */
export const BRIDGE_REQUEST_TYPES = [
  'LIST_RESOURCES',
  'GET_SCHEMA',
  'READ_CONFIG',
  'VALIDATE_CONFIG',
  'APPLY_CONFIG',
  'GET_CATALOG',
] as const;
export type BridgeRequestType = (typeof BRIDGE_REQUEST_TYPES)[number];

export const PROTOCOL_ERROR_CODES = [
  'BAD_REQUEST',
  'UNSUPPORTED_PROTOCOL',
  'AUTH_FAILED',
  'INSTALLATION_REVOKED',
  'NOT_PAIRED',
  'ALREADY_PAIRED',
  'UNKNOWN_RESOURCE',
  'UNSUPPORTED_OPERATION',
  'PAYLOAD_TOO_LARGE',
  'EXPIRED',
  'BUSY',
  'TIMEOUT',
  'RATE_LIMITED',
  'OPERATION_IN_PROGRESS',
  'INTERNAL',
] as const;
export const ProtocolErrorCodeSchema = z.enum(PROTOCOL_ERROR_CODES);
export type ProtocolErrorCode = z.infer<typeof ProtocolErrorCodeSchema>;

export const RequestIdSchema = z.string().regex(/^[A-Za-z0-9_-]{1,64}$/);

export const ProtocolErrorSchema = z.object({
  code: ProtocolErrorCodeSchema,
  message: z.string().max(1000),
});
export type ProtocolError = z.infer<typeof ProtocolErrorSchema>;

/**
 * Common frame shape. `requestId` correlates a request with its RESULT/ERROR (same value).
 * Events (CHALLENGE, PAIRED, REVOKED, RESOURCES_CHANGED) carry a fresh requestId.
 */
export const EnvelopeSchema = z.object({
  protocol: z.literal(PROTOCOL_VERSION),
  type: MessageTypeSchema,
  requestId: RequestIdSchema,
  /** Present on resource-scoped requests (GET_SCHEMA, READ_CONFIG, VALIDATE_CONFIG, APPLY_CONFIG). */
  resourceId: ResourceIdSchema.optional(),
  /** Requests only: the receiver drops the request with EXPIRED if it cannot start it within ttlMs of receipt. */
  ttlMs: z.int().min(1).max(120_000).optional(),
  payload: z.record(z.string(), z.unknown()).optional(),
  /** ERROR only. */
  error: ProtocolErrorSchema.optional(),
});
export type Envelope = z.infer<typeof EnvelopeSchema>;

// ---- Handshake -------------------------------------------------------------------------------

/** Backend -> bridge, first frame after the WebSocket opens. */
export const ChallengePayloadSchema = z.object({
  nonce: z.string().regex(/^[A-Za-z0-9_-]{43}$/),
  supportedProtocols: z.array(z.int()).min(1),
  serverTime: z.int(),
});
export type ChallengePayload = z.infer<typeof ChallengePayloadSchema>;

export const ServerPlatformSchema = z.object({
  /** Display name configured in the bridge; suggested as the server's name when pairing. */
  name: z.string().min(1).max(64),
  platform: z.string().min(1).max(64),
  platformVersion: z.string().max(128),
  minecraftVersion: z.string().max(32),
});
export type ServerPlatform = z.infer<typeof ServerPlatformSchema>;

/** Bridge -> backend, answers CHALLENGE (same requestId). */
export const HelloPayloadSchema = z.object({
  installationId: z.uuid(),
  /** Base64 (standard, padded) X.509 SubjectPublicKeyInfo DER of the installation's Ed25519 key. */
  publicKey: z.string().min(40).max(200),
  /** Base64 Ed25519 signature of AUTH_SIGNATURE_PREFIX + nonce + '\n' + installationId (UTF-8). */
  signature: z.string().min(80).max(200),
  bridgeVersion: z.string().min(1).max(64),
  apiVersion: z.int().min(1),
  server: ServerPlatformSchema,
});
export type HelloPayload = z.infer<typeof HelloPayloadSchema>;

export const PairedServerSchema = z.object({
  id: z.uuid(),
  displayName: z.string().min(1).max(64),
});
export type PairedServer = z.infer<typeof PairedServerSchema>;

/** Backend -> bridge, answers HELLO (same requestId). */
export const WelcomePayloadSchema = z.object({
  sessionId: z.uuid(),
  status: z.enum(['PAIRED', 'UNPAIRED']),
  /** Present when status is PAIRED. */
  server: PairedServerSchema.optional(),
  heartbeatIntervalMs: z.int().min(1000),
  heartbeatTimeoutMs: z.int().min(1000),
  maxMessageBytes: z.int().min(1024),
  serverTime: z.int(),
});
export type WelcomePayload = z.infer<typeof WelcomePayloadSchema>;

/** HEARTBEAT has an empty payload. The bridge sends it; the backend echoes it with the same requestId. */
export const HeartbeatPayloadSchema = z.object({}).strict();

// ---- Pairing ---------------------------------------------------------------------------------

/** Bridge -> backend request (empty payload), answered by RESULT with this payload. */
export const PairingCodeResultSchema = z.object({
  /** Formatted as XXXX-XXXX. */
  code: z.string().regex(/^[2-9A-HJKMNP-Z]{4}-[2-9A-HJKMNP-Z]{4}$/),
  expiresAt: z.iso.datetime(),
});
export type PairingCodeResult = z.infer<typeof PairingCodeResultSchema>;

/** Backend -> bridge event once a pairing code is claimed. */
export const PairedPayloadSchema = z.object({
  server: PairedServerSchema,
  /** Masked email of the claiming account, for example j***@example.com. */
  accountLabel: z.string().max(320),
});
export type PairedPayload = z.infer<typeof PairedPayloadSchema>;

/** Backend -> bridge event before closing with INSTALLATION_REVOKED. */
export const RevokedPayloadSchema = z.object({
  reason: z.string().max(300),
});
export type RevokedPayload = z.infer<typeof RevokedPayloadSchema>;

// ---- Resource requests (backend -> bridge) ----------------------------------------------------

export const ListResourcesResultSchema = z.object({
  resources: z.array(ResourceSummarySchema).max(1000),
});
export type ListResourcesResult = z.infer<typeof ListResourcesResultSchema>;

export const GetSchemaResultSchema = z.object({
  schema: SchemaDescriptorSchema,
});
export type GetSchemaResult = z.infer<typeof GetSchemaResultSchema>;

export const ReadConfigResultSchema = z.object({
  revision: z.string().min(1).max(128),
  /** Editable values. Sensitive fields are omitted and listed in `redacted`. */
  values: ConfigObjectSchema,
  redacted: z.array(RedactedFieldSchema),
});
export type ReadConfigResult = z.infer<typeof ReadConfigResultSchema>;

/**
 * Proposed values for VALIDATE_CONFIG and APPLY_CONFIG: the complete values tree. Sensitive fields are
 * omitted to keep the stored value, or present (writeOnly fields only) to replace it.
 */
export const ValidateConfigPayloadSchema = z.object({
  baseRevision: z.string().min(1).max(128),
  values: ConfigObjectSchema,
});
export type ValidateConfigPayload = z.infer<typeof ValidateConfigPayloadSchema>;

export const ActorSchema = z.object({
  id: z.uuid(),
  label: z.string().max(320),
});
export type Actor = z.infer<typeof ActorSchema>;

export const ApplyConfigPayloadSchema = z.object({
  operationId: z.uuid(),
  baseRevision: z.string().min(1).max(128),
  values: ConfigObjectSchema,
  actor: ActorSchema,
});
export type ApplyConfigPayload = z.infer<typeof ApplyConfigPayloadSchema>;

export const ValidateConfigResultSchema = ValidateOutcomeSchema;
export type ValidateConfigResult = z.infer<typeof ValidateConfigResultSchema>;

export const ApplyConfigResultSchema = ApplyOutcomeSchema;
export type ApplyConfigResult = z.infer<typeof ApplyConfigResultSchema>;

export const GetCatalogPayloadSchema = z.object({
  catalog: z.literal('materials'),
});
export type GetCatalogPayload = z.infer<typeof GetCatalogPayloadSchema>;

export const GetCatalogResultSchema = z.object({
  catalog: z.literal('materials'),
  minecraftVersion: z.string().max(32),
  entries: z.array(MaterialEntrySchema).max(10_000),
});
export type GetCatalogResult = z.infer<typeof GetCatalogResultSchema>;

// ---- Events (bridge -> backend) ---------------------------------------------------------------

/** Sent whenever registrations change or a plugin calls ResourceRegistration.notifyChanged(). */
export const ResourcesChangedPayloadSchema = z.object({
  resources: z.array(ResourceSummarySchema).max(1000),
  /** Resource ids whose values or schema changed outside ComfiConf. */
  changed: z.array(ResourceIdSchema),
});
export type ResourcesChangedPayload = z.infer<typeof ResourcesChangedPayloadSchema>;

/** RESULT payload schema for each request type. */
export const RESULT_PAYLOAD_SCHEMAS = {
  LIST_RESOURCES: ListResourcesResultSchema,
  GET_SCHEMA: GetSchemaResultSchema,
  READ_CONFIG: ReadConfigResultSchema,
  VALIDATE_CONFIG: ValidateConfigResultSchema,
  APPLY_CONFIG: ApplyConfigResultSchema,
  GET_CATALOG: GetCatalogResultSchema,
} as const satisfies Record<BridgeRequestType, z.ZodType>;

export type BridgeResultPayload<T extends BridgeRequestType> = z.infer<
  (typeof RESULT_PAYLOAD_SCHEMAS)[T]
>;
